import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiveItem = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [receives, setReceives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    receive_number: `RCV-${Date.now()}`,
    receive_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    supplier_name: '',
    invoice_number: '',
    notes: '',
    received_by: 'Admin',
    paid_amount: 0,
    payment_status: 'Unpaid'
  });
  const [selectedItems, setSelectedItems] = useState([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
    fetchReceives();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await apiClient.get('/api/inventory/items');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/inventory/suppliers');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchReceives = async () => {
    try {
      const response = await apiClient.get('/api/inventory/stock-receives');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setReceives(data);
    } catch (error) {
      console.error('Error fetching receives:', error);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.name
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...selectedItems];
    updated[index][field] = value;

    if (field === 'item_id') {
      const item = items.find(i => i.id === value);
      if (item) {
        updated[index].item_name = item.item_name;
        updated[index].unit_price = item.unit_price;
        updated[index].total = updated[index].quantity * item.unit_price;
      }
    } else if (field === 'quantity' || field === 'unit_price') {
      updated[index].total = updated[index].quantity * updated[index].unit_price;
    }

    setSelectedItems(updated);
  };

  const addItemRow = () => {
    setSelectedItems([...selectedItems, { item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeItemRow = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validItems = selectedItems.filter(item => item.item_id && item.quantity > 0);
    if (validItems.length === 0) {
      alert('Please add at least one item');
      setLoading(false);
      return;
    }

    const totalAmount = calculateTotal();
    const dueAmount = totalAmount - formData.paid_amount;

    try {
      const response = await apiClient.post('/api/inventory/stock-receives', {
        ...formData,
        items: validItems,
        total_amount: totalAmount,
        due_amount: dueAmount,
        payment_status: dueAmount <= 0 ? 'Paid' : (formData.paid_amount > 0 ? 'Partial' : 'Unpaid')
      });

      if (response.status === 200 || response.data) {
        alert('Stock received successfully!');
        fetchReceives();
        fetchItems();
        // Reset form
        setFormData({
          receive_number: `RCV-${Date.now()}`,
          receive_date: new Date().toISOString().split('T')[0],
          supplier_id: '',
          supplier_name: '',
          invoice_number: '',
          notes: '',
          received_by: 'Admin',
          paid_amount: 0,
          payment_status: 'Unpaid'
        });
        setSelectedItems([{ item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]);
      } else {
        alert('Failed to receive stock');
      }
    } catch (error) {
      console.error('Error receiving stock:', error);
      alert(error.response?.data?.detail || 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReceive = async (receive) => {
    if (!window.confirm(`Delete stock receive ${receive.receive_number}?\n\nThis will:\n- Remove items from inventory\n- Reverse supplier balance\n- Delete linked expense + cash entries`)) return;
    try {
      await apiClient.delete(`/api/inventory/stock-receives/${receive.id}`);
      alert('Stock receive deleted and accounting rolled back.');
      fetchReceives();
      fetchItems();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Receive Items</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Receive Number</Label>
                <Input value={formData.receive_number} disabled />
              </div>
              <div>
                <Label>Receive Date *</Label>
                <Input
                  type="date"
                  required
                  value={formData.receive_date}
                  onChange={(e) => setFormData({ ...formData, receive_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="Supplier's invoice #"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier *</Label>
                <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} - {supplier.company_name || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Received By</Label>
                <Input
                  value={formData.received_by}
                  onChange={(e) => setFormData({ ...formData, received_by: e.target.value })}
                />
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Items</h3>
                <Button type="button" size="sm" onClick={addItemRow}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>

              {selectedItems.map((item, index) => (
                <div key={index} className="grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label>Item</Label>
                    <Select value={item.item_id} onValueChange={(value) => handleItemChange(index, 'item_id', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((i) => (
                          <SelectItem key={i.id} value={i.id}>
                            {i.item_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Total</Label>
                    <Input value={item.total} disabled />
                  </div>
                  <div>
                    <Button type="button" size="sm" variant="destructive" onClick={() => removeItemRow(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="space-y-2 pt-3 border-t">
                <div className="flex justify-between text-lg">
                  <span>Total Amount:</span>
                  <span className="font-bold">₹{calculateTotal()}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Paid Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.paid_amount}
                      onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Due Amount</Label>
                    <Input value={calculateTotal() - formData.paid_amount} disabled />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes"
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Processing...' : 'Receive Stock'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Stock Receives</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Receive #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Supplier</th>
                  <th className="text-left p-3">Total Amount</th>
                  <th className="text-left p-3">Due Amount</th>
                  <th className="text-left p-3">Payment Status</th>
                  <th className="text-left p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {receives.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-4 text-gray-500">
                      No stock receives found.
                    </td>
                  </tr>
                ) : (
                  receives.map((receive) => (
                    <tr key={receive.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{receive.receive_number}</td>
                      <td className="p-3">{formatDate(receive.receive_date)}</td>
                      <td className="p-3">{receive.supplier_name}</td>
                      <td className="p-3">₹{receive.total_amount}</td>
                      <td className="p-3">
                        <span className={receive.due_amount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          ₹{receive.due_amount}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          receive.payment_status === 'Paid' ? 'bg-green-100 text-green-800' :
                          receive.payment_status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {receive.payment_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleDeleteReceive(receive)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete & rollback accounting"
                          data-testid={`delete-receive-${receive.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReceiveItem;